import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, IconButton } from '@/components/ui/Button'
import { Skeleton } from '@/components/ui/Skeleton'
import { intlLocale } from '@/i18n'
import { cn } from '@/utils/cn'
import { awayByDay, daysOfMonth, parseYmd, toYmd, weekdaysMondayFirst } from '../dates'
import type { LeaveCalendarData, LeaveCalendarEntry } from '../types'
import { leaveSoft } from '../colours'

interface Props {
  month: Date
  onMonthChange: (month: Date) => void
  data: LeaveCalendarData | undefined
  loading?: boolean
  /** Called with the request id when an entry is activated. */
  onSelect?: (requestId: number) => void
  /** Max entries shown per day cell before "+N more". */
  maxPerDay?: number
}

function EntryChip({ entry, onSelect }: { entry: LeaveCalendarEntry; onSelect?: (id: number) => void }) {
  const { t } = useTranslation('leave')
  const pending = entry.status === 'pending'
  const label = `${entry.personName}, ${entry.type.name}${entry.halfDay ? `, ${t('half day')}` : ''}${pending ? `, ${t('pending approval')}` : ''}`
  const className = cn(
    'block w-full truncate rounded-xs px-1.5 py-0.5 text-left text-2xs font-medium ring-1 ring-inset',
    leaveSoft[entry.type.colour],
    pending && 'border border-dashed border-current opacity-80',
  )
  const content = (
    <>
      <span aria-hidden="true">{entry.personName}</span>
      <span className="sr-only">{label}</span>
    </>
  )
  return onSelect ? (
    <button
      type="button"
      className={cn(className, 'hover:brightness-95')}
      onClick={() => onSelect(entry.requestId)}
      title={label}
    >
      {content}
    </button>
  ) : (
    <span className={className} title={label}>
      {content}
    </span>
  )
}

/**
 * Month view of who is away. A semantic table on larger screens; on phones a list of the days
 * that have someone away. Pending requests are drawn dashed.
 */
export function LeaveCalendar({ month, onMonthChange, data, loading, onSelect, maxPerDay = 3 }: Props) {
  const { t } = useTranslation('leave')
  // Formatters follow the interface language (useTranslation re-renders on change).
  const locale = intlLocale()
  const { monthLabel, dayLabel, weekdays } = useMemo(
    () => ({
      monthLabel: new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' }),
      dayLabel: new Intl.DateTimeFormat(locale, { weekday: 'long', day: 'numeric', month: 'long' }),
      weekdays: weekdaysMondayFirst(),
    }),
    [locale],
  )
  const days = useMemo(() => daysOfMonth(month), [month])
  const byDay = useMemo(() => awayByDay(days, data), [days, data])
  const today = toYmd(new Date())
  const closed = data?.closedWeekdays ?? []

  const weeks = useMemo(() => {
    const first = parseYmd(days[0] ?? toYmd(month))
    const lead = (first.getDay() + 6) % 7
    const cells: (string | null)[] = [...Array.from({ length: lead }, () => null), ...days]
    while (cells.length % 7) cells.push(null)
    return Array.from({ length: cells.length / 7 }, (_, i) => cells.slice(i * 7, i * 7 + 7))
  }, [days, month])

  const title = monthLabel.format(month)

  return (
    <div>
      <div className="flex items-center justify-between gap-2 border-b border-line px-4 py-3">
        <h3 className="text-sm font-semibold text-ink" aria-live="polite">
          {title}
        </h3>
        <div className="flex items-center gap-1">
          <IconButton
            label={t('Previous month')}
            icon={<ChevronLeft />}
            size="icon-sm"
            onClick={() => onMonthChange(new Date(month.getFullYear(), month.getMonth() - 1, 1))}
          />
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onMonthChange(new Date(new Date().getFullYear(), new Date().getMonth(), 1))}
          >
            {t('Today')}
          </Button>
          <IconButton
            label={t('Next month')}
            icon={<ChevronRight />}
            size="icon-sm"
            onClick={() => onMonthChange(new Date(month.getFullYear(), month.getMonth() + 1, 1))}
          />
        </div>
      </div>

      {loading && !data ? (
        <div className="p-4">
          <Skeleton className="h-64 w-full" />
        </div>
      ) : (
        <>
          <table className="hidden w-full table-fixed border-collapse md:table">
            <caption className="sr-only">
              {t('Staff and students away in {{month}}', { month: title })}
            </caption>
            <thead>
              <tr>
                {weekdays.map((w) => (
                  <th
                    key={w.short}
                    scope="col"
                    abbr={w.long}
                    className="border-b border-line px-2 py-2 text-left text-2xs font-semibold tracking-wide text-muted uppercase"
                  >
                    {w.short}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {weeks.map((week, wi) => (
                <tr key={wi}>
                  {week.map((day, di) => {
                    if (!day) return <td key={di} className="h-24 border-b border-line bg-surface-muted/40" />
                    const entries = byDay.get(day) ?? []
                    const isClosed = closed.includes(parseYmd(day).getDay())
                    return (
                      <td
                        key={day}
                        className={cn(
                          'h-24 border-b border-line p-1.5 align-top',
                          di > 0 && 'border-l',
                          isClosed && 'bg-surface-muted/60',
                        )}
                      >
                        <div className="mb-1 flex items-center justify-between">
                          <span
                            className={cn(
                              'grid size-6 place-items-center rounded-full text-xs tabular',
                              day === today
                                ? 'bg-brand-600 font-semibold text-white'
                                : isClosed
                                  ? 'text-subtle'
                                  : 'text-ink-soft',
                            )}
                          >
                            <span aria-hidden="true">{parseYmd(day).getDate()}</span>
                            <span className="sr-only">{dayLabel.format(parseYmd(day))}</span>
                          </span>
                          {isClosed ? <span className="text-2xs text-subtle">{t('Closed')}</span> : null}
                        </div>
                        <ul className="space-y-0.5">
                          {entries.slice(0, maxPerDay).map((e) => (
                            <li key={e.requestId}>
                              <EntryChip entry={e} onSelect={onSelect} />
                            </li>
                          ))}
                        </ul>
                        {entries.length > maxPerDay ? (
                          <p className="mt-0.5 text-2xs text-muted">
                            {t('+{{count}} more', { count: entries.length - maxPerDay })}
                          </p>
                        ) : null}
                      </td>
                    )
                  })}
                </tr>
              ))}
            </tbody>
          </table>

          <div className="md:hidden">
            {byDay.size === 0 ? (
              <p className="px-4 py-8 text-center text-sm text-muted">{t('Nobody is away this month.')}</p>
            ) : (
              <ol
                className="divide-y divide-line"
                aria-label={t('Days with people away in {{month}}', { month: title })}
              >
                {[...byDay.entries()].map(([day, entries]) => (
                  <li key={day} className="px-4 py-3">
                    <p
                      className={cn(
                        'text-xs font-semibold',
                        day === today ? 'text-brand-700' : 'text-ink-soft',
                      )}
                    >
                      {dayLabel.format(parseYmd(day))}
                    </p>
                    <ul className="mt-1.5 grid gap-1">
                      {entries.map((e) => (
                        <li key={e.requestId}>
                          <EntryChip entry={e} onSelect={onSelect} />
                        </li>
                      ))}
                    </ul>
                  </li>
                ))}
              </ol>
            )}
          </div>
        </>
      )}
    </div>
  )
}
